/**
 * "Checked 12 seconds ago", with a manual refresh.
 *
 * The relative time re-renders on its own clock rather than only when data
 * arrives, so a dashboard that has silently stopped polling says "4 minutes
 * ago" and keeps counting instead of freezing on a reassuring "just now".
 */

import { useEffect, useState } from 'react';

import { Button } from './ui/Button';
import { RenewIcon } from './ui/Icon';

import { formatDateTime, formatRelative, isoAttribute, parseDate } from '../lib/format';

/** A ticking clock, used only for the freshness line. */
function useNow(intervalMs = 1000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), intervalMs);
    return () => window.clearInterval(timer);
  }, [intervalMs]);
  return now;
}

export function Freshness({
  checkedAt,
  refreshing = false,
  onRefresh,
  label = 'Checked',
  staleAfterMs = 60_000,
}: {
  checkedAt: string | Date | null | undefined;
  refreshing?: boolean;
  onRefresh?: () => void;
  label?: string;
  staleAfterMs?: number;
}) {
  const now = useNow();
  const date = checkedAt instanceof Date ? checkedAt : parseDate(checkedAt ?? null);
  const ageMs = date ? now - date.getTime() : null;
  const stale = ageMs !== null && ageMs > staleAfterMs;

  return (
    <div className="freshness-row">
      <p className={`freshness${stale ? ' freshness--stale' : ''}`}>
        {label}{' '}
        {date ? (
          <time dateTime={isoAttribute(date)} title={formatDateTime(date)}>
            {formatRelative(date, now)}
          </time>
        ) : (
          'never'
        )}
      </p>
      {onRefresh ? (
        <Button
          kind="ghost"
          size="sm"
          renderIcon={RenewIcon}
          onClick={onRefresh}
          disabled={refreshing}
        >
          {refreshing ? 'Refreshing…' : 'Refresh'}
        </Button>
      ) : null}
    </div>
  );
}
